import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import JobCarousel from "./JobCarousel";
import InvestmentCard from "./InvestmentCard";
import { chatData } from "@/data/chatData";
import { investmentsData } from "@/data/investmentsData";

const ChatInterface = ({ 
  messages, 
  isTyping, 
  userInput, 
  setUserInput, 
  handleSendMessage, 
  handleOptionClick, 
  resetChat,
  showSuggestions,
  getFollowUpSuggestions
}) => {
  const messagesEndRef = useRef(null);
  const [carouselMounted, setCarouselMounted] = useState(false);
  const [investmentsMounted, setInvestmentsMounted] = useState(false);
  const [expandedInvestment, setExpandedInvestment] = useState(null);
  const [ramblingsLoaded, setRamblingsLoaded] = useState(false);
  const [expandedRambling, setExpandedRambling] = useState(null);
  
  // Scroll to bottom of chat whenever messages change
  useEffect(() => {
    scrollToBottom();
    
    // Check if we need to mount the job carousel
    if (!carouselMounted) {
      const shouldMountCarousel = messages.some(msg => 
        msg.sender === 'ai' && 
        msg.content.includes('id="job-carousel-container"')
      );
      
      if (shouldMountCarousel) {
        setTimeout(() => {
          const container = document.getElementById('job-carousel-container');
          if (container) {
            setCarouselMounted(true);
          }
        }, 100);
      }
    }
    
    // Check if we need to mount the investments grid
    if (!investmentsMounted) {
      const shouldMountInvestments = messages.some(msg => 
        msg.sender === 'ai' && 
        msg.content.includes('id="investments-container"')
      );
      
      if (shouldMountInvestments) {
        setTimeout(() => {
          const container = document.getElementById('investments-container');
          if (container) {
            setInvestmentsMounted(true);
          }
        }, 100);
      }
    }
    
    // Check if we need to load ramblings
    if (!ramblingsLoaded) {
      const shouldLoadRamblings = messages.some(msg => 
        msg.sender === 'ai' && 
        msg.content.includes('id="ramblings-container"')
      );
      
      if (shouldLoadRamblings) {
        setTimeout(() => {
          const container = document.getElementById('ramblings-container');
          if (container) {
            setRamblingsLoaded(true);
          }
        }, 100);
      }
    }
  }, [messages, isTyping, carouselMounted, investmentsMounted, ramblingsLoaded]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSendMessage();
    }
  };

  return (
    <section id="chat-section" className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20 max-w-2xl">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Chat header */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <div className="flex-shrink-0 mr-3 flex" style={{ paddingTop: '4px' }}>
              <div className="h-8 w-8 rounded-full overflow-hidden border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700">
                <img 
                  src="/images/alex_ai_avatar.png"
                  alt="Alex AI"
                  className="w-full h-full object-cover"
                  loading="eager"
                  width="32"
                  height="32"
                />
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Alex AI</h3>
            </div>
          </div>
          <div>
            <button 
              onClick={resetChat} 
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Reset chat"
            >
              <i className="fas fa-redo-alt"></i>
            </button>
          </div>
        </div>
        
        {/* Chat messages */}
        <div className="p-4 h-[400px] overflow-y-auto bg-gray-50 dark:bg-gray-600 space-y-4">
          <div>
            {messages.map((message, index) => (
              <div 
                key={index}
                className={`chat-message mb-4 ${message.sender === 'ai' ? 'ai-message' : 'user-message'}`}
              >
                {message.sender === 'ai' ? (
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-3 flex" style={{ paddingTop: '4px' }}>
                      <div className="h-8 w-8 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-700">
                        <img 
                          src="/images/alex_ai_avatar.png"
                          alt="Alex AI"
                          className="w-full h-full object-cover"
                          loading="eager"
                          width="32"
                          height="32"
                        />
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-lg rounded-tl-none max-w-[85%] text-gray-900 dark:text-gray-100">
                      <div dangerouslySetInnerHTML={{ __html: message.content }} />
                      {message.content.includes('id="job-carousel-container"') && (
                        <div id={`job-carousel-container-${index}`}>
                          <JobCarousel jobs={chatData.jobData} />
                        </div>
                      )}
                      
                      {message.content.includes('id="investments-container"') && (
                        <div id={`investments-container-${index}`} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          {investmentsData.map((investment, idx) => (
                            <InvestmentCard 
                              key={idx}
                              company={investment.company}
                              logoSrc={investment.logoSrc}
                              hoverLogoSrc={investment.hoverLogoSrc}
                              stage={investment.stage}
                              description={investment.description}
                              coInvestors={investment.coInvestors}
                              expanded={expandedInvestment === investment.company}
                              onToggle={setExpandedInvestment}
                            />
                          ))}
                        </div>
                      )}
                      
                      {message.content.includes('id="ramblings-container"') && (
                        <div id={`ramblings-container-${index}`} className="space-y-3 mt-3">
                          {chatData.ramblingData.map((rambling, idx) => (
                            <div 
                              key={idx} 
                              className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 ${
                                expandedRambling === rambling.id 
                                  ? 'shadow-lg' 
                                  : 'hover:shadow-md cursor-pointer'
                              }`}
                            >
                              {expandedRambling === rambling.id ? (
                                <div className="p-4">
                                  <div className="flex justify-between items-start mb-3">
                                    <h4 className="text-base font-medium">{rambling.title}</h4>
                                    <button 
                                      onClick={() => setExpandedRambling(null)}
                                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex-shrink-0 ml-2"
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">{rambling.date}</div>
                                  <div className="article-content" dangerouslySetInnerHTML={{ __html: rambling.content }} />
                                </div>
                              ) : (
                                <div className="p-4" onClick={() => setExpandedRambling(rambling.id)}>
                                  <h4 className="text-base font-medium mb-1">{rambling.title}</h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{rambling.summary}</p>
                                  <div className="mt-2 flex justify-between items-center">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{rambling.date}</span>
                                    <span className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Read more →</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-end">
                    <div className="p-3 bg-gray-100 dark:bg-gray-600 rounded-lg rounded-tr-none max-w-[85%] text-gray-900 dark:text-gray-100">
                      <p className="text-sm">{message.content}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {/* Initial options - only shown when no messages yet except welcome */}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 ml-14 mb-4">
                <button 
                  onClick={() => handleOptionClick('about')} 
                  className="chat-option px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
                >
                  About Alex
                </button>
                <button 
                  onClick={() => handleOptionClick('investments')} 
                  className="chat-option px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
                >
                  Investments
                </button>
                <button 
                  onClick={() => handleOptionClick('projects')} 
                  className="chat-option px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
                >
                  Builder Journey
                </button>
                <button 
                  onClick={() => handleOptionClick('ramblings')} 
                  className="chat-option px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
                >
                  Ramblings
                </button>
              </div>
            )}
            
            {/* Follow-up suggestions after each AI response */}
            {messages.length > 1 && !isTyping && showSuggestions && messages[messages.length - 1].sender === 'ai' && (
              <div className="flex flex-wrap gap-2 ml-14 mb-4 mt-1">
                <div className="w-full mb-1 text-xs text-gray-500 dark:text-gray-400">Try asking:</div>
                {getFollowUpSuggestions().map((suggestion) => (
                  <button 
                    key={suggestion.id}
                    onClick={() => handleOptionClick(suggestion.id)} 
                    className="chat-option px-3 py-1.5 text-xs bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors text-gray-900 dark:text-gray-100"
                  >
                    {suggestion.label}
                  </button>
                ))}
              </div>
            )}
            
            {/* Typing indicator */}
            {isTyping && (
              <div className="chat-message mb-4 ai-message">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3 flex" style={{ paddingTop: '4px' }}>
                    <div className="h-8 w-8 rounded-full overflow-hidden border border-gray-200 dark:border-gray-600">
                      <img 
                        src="/images/alex_ai_avatar.png"
                        alt="Alex AI"
                        className="w-full h-full object-cover"
                        loading="eager"
                        width="32"
                        height="32"
                      />
                    </div>
                  </div>
                  <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-100 dark:border-gray-600 rounded-lg rounded-tl-none">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* Input area */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center">
            <input 
              id="userInput"
              type="text" 
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message or select an option above..." 
              className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-full focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-500 transition-colors bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100" 
            />
            <button 
              onClick={handleSendMessage} 
              className="ml-2 p-2 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-full hover:bg-gray-600 dark:hover:bg-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500 transition-colors"
              aria-label="Send message"
            >
              <i className="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatInterface;